/**
 * Sign-in modal component for magic link authentication
 */

import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigationStore } from '../store/useNavigationStore';
import { TokenInputModal } from './TokenInputModal';
import './SignInModal.css';

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SignInModal({ isOpen, onClose }: SignInModalProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showTokenInput, setShowTokenInput] = useState(false);
  const { requestMagicLink } = useAuth();
  const navigate = useNavigationStore((s) => s.navigate);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !email.includes('@')) {
      return;
    }

    setIsLoading(true);

    const success = await requestMagicLink(email);

    if (success) {
      setEmail('');
      // Show token input for localhost development
      setShowTokenInput(true);
    }

    setIsLoading(false);
  };

  const handleClose = () => {
    setEmail('');
    setShowTokenInput(false);
    onClose();
  };

  const handleLoginSuccess = () => {
    setShowTokenInput(false);
    setEmail('');
    onClose();
    navigate('account');
  };

  const handleTokenModalClose = () => {
    setShowTokenInput(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: clicking overlay closes modal */}
      <div
        className="signin-overlay"
        onClick={handleClose}
        onKeyDown={(e) => e.key === 'Escape' && handleClose()}
      >
        <div
          className="signin-modal"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-labelledby="signin-title"
          aria-modal="true"
        >
          <div className="signin-header">
            <h2 id="signin-title">Sign In to Refinevoice</h2>
            <button type="button" className="signin-close" onClick={handleClose} aria-label="Close">
              ✕
            </button>
          </div>

          <div className="signin-body">
            <p className="signin-description">
              Sign in for longer voice recordings (5 minutes instead of 30 seconds).
            </p>

            <form onSubmit={handleSubmit} className="signin-form">
              <div className="form-group">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary signin-submit-btn"
                disabled={isLoading || !email.trim() || !email.includes('@')}
              >
                <span>{isLoading ? 'Sending...' : 'Send Magic Link'}</span>
              </button>
            </form>

            <div className="signin-alternatives">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowTokenInput(true)}
              >
                🔧 Enter Token Instead
              </button>
            </div>

            <div className="signin-terms">
              <label className="checkbox-label">
                <input type="checkbox" required defaultChecked />
                <span className="checkbox-text">
                  Email needed for authentication. We may send product updates.
                </span>
              </label>
              <p className="signin-help">
                📧 Email needed for authentication token
                <br />
                <small>We'll email you a magic link for instant sign-in. No password needed!</small>
                <br />
                <small>
                  For localhost: Check your email for the token or click the magic link.
                </small>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Token Input Modal */}
      <TokenInputModal
        isOpen={showTokenInput}
        onClose={handleTokenModalClose}
        onSuccess={handleLoginSuccess}
      />
    </>
  );
}
